import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { errorMessage, formatDate } from '../lib/format';
import { usePlan } from '../lib/queries';
import { haptic, payWithStars } from '../lib/telegram';
import type { Plan } from '../lib/types';
import { Icon, type IconName } from './Icon';
import { KhqrPaySheet } from './KhqrPay';
import { Sheet } from './Sheet';
import { useToast } from './Toast';

/** Waits for the bot to record the payment (it arrives by webhook, a moment after "paid"). */
async function waitForPro(tries = 10): Promise<Plan | null> {
  for (let i = 0; i < tries; i++) {
    const plan = await api.plan().catch(() => null);
    if (plan?.tier === 'pro') return plan;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  return null;
}

/** Starts the Telegram Stars checkout for Pro. */
export function useUpgrade(onDone?: () => void) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const upgrade = async () => {
    setBusy(true);
    haptic.tap();
    try {
      const { invoiceLink } = await api.proCheckout();
      const status = await payWithStars(invoiceLink);
      if (status === 'unavailable') return toast(t('pro.openInTelegram'), 'error');
      if (status === 'cancelled') return;
      if (status === 'failed') return toast(t('pro.failed'), 'error');
      haptic.success();
      const plan = await waitForPro();
      await queryClient.invalidateQueries({ queryKey: ['plan'] });
      toast(plan ? t('pro.welcome') : t('pro.processing'));
      onDone?.();
    } catch (error) {
      toast(errorMessage(error, t), 'error');
    } finally {
      setBusy(false);
    }
  };
  return { upgrade, busy };
}

const BENEFITS: Array<{ icon: IconName; key: string }> = [
  { icon: 'camera', key: 'pro.benefitScans' },
  { icon: 'heart', key: 'pro.benefitSupport' },
  { icon: 'checkCircle', key: 'pro.benefitFree' },
];

/** "Get Pro" bottom sheet: what you get, the price, and the Stars checkout. */
export function UpgradeSheet({ open, onClose, reason }: { open: boolean; onClose: () => void; reason?: string }) {
  const { t } = useTranslation();
  const plan = usePlan();
  const { upgrade, busy } = useUpgrade(onClose);
  const [khqrOpen, setKhqrOpen] = useState(false);
  const stars = plan.data?.priceStars ?? 100;
  const khqr = plan.data?.khqr?.available ? plan.data.khqr : null;

  return (
    <Sheet open={open} onClose={onClose} title={t('pro.title')}>
      <div className="pro-hero">
        <span className="pro-badge">
          <Icon name="sparkles" size={16} />
          Pro
        </span>
        {reason && <p className="pro-reason">{reason}</p>}
        {khqr ? (
          <div className="pro-price num">
            ${khqr.priceUsd}
            <span>{t('pro.perMonthUsd')}</span>
          </div>
        ) : (
          <div className="pro-price num">
            <Icon name="star" size={22} className="pro-star" />
            {stars}
            <span>{t('pro.perMonth')}</span>
          </div>
        )}
      </div>
      <ul className="pro-benefits">
        {BENEFITS.map((b) => (
          <li key={b.key}>
            <span className="icon-tile icon-tile--sm icon-tile--ink" aria-hidden>
              <Icon name={b.icon} size={16} />
            </span>
            {t(b.key)}
          </li>
        ))}
      </ul>
      {khqr && (
        <>
          <button className="btn btn--primary btn--block pay-khqr" onClick={() => (haptic.tap(), setKhqrOpen(true))}>
            <Icon name="qr" size={18} />
            {t('pro.payKhqr', { price: `$${khqr.priceUsd}` })}
          </button>
          <div className="muted small" style={{ textAlign: 'center', marginTop: -4 }}>
            {t('pro.payKhqrBanks')}
          </div>
          <div className="pay-or">{t('pro.or')}</div>
        </>
      )}
      <button
        className={`btn btn--block ${khqr ? 'btn--soft' : 'btn--primary'}`}
        disabled={busy}
        onClick={() => void upgrade()}
      >
        <Icon name={busy ? 'loader' : 'star'} size={18} className={busy ? 'spin' : undefined} />
        {khqr ? t('pro.payStars', { stars }) : t('pro.cta', { stars })}
      </button>
      <KhqrPaySheet
        open={khqrOpen}
        onClose={() => {
          setKhqrOpen(false);
          if (plan.data?.tier === 'pro') onClose();
        }}
        create={api.proKhqr}
      />
      <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>
        {khqr ? t('pro.fineprintBoth') : t('pro.fineprint')}
      </p>
    </Sheet>
  );
}

/** Profile card: current plan, this month's scans, and the way to Pro. */
export function PlanCard() {
  const { t, i18n } = useTranslation();
  const plan = usePlan();
  const [open, setOpen] = useState(false);
  if (!plan.data) return null;
  const p = plan.data;
  const isPro = p.tier === 'pro';
  const left = Math.max(0, p.scansLimit - p.scansUsed);

  return (
    <div className={`card plan-card ${isPro ? 'plan-card--pro' : ''}`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div className="plan-label">{t('pro.yourPlan')}</div>
          <div className="plan-name">
            {isPro ? (
              <>
                <Icon name="sparkles" size={18} /> Pro
              </>
            ) : (
              t('pro.free')
            )}
          </div>
        </div>
        {isPro && p.proUntil && (
          <div className="plan-until small">{t('pro.until', { date: formatDate(p.proUntil.slice(0, 10), i18n.language) })}</div>
        )}
      </div>

      <div className="plan-usage">
        <div className="row small" style={{ justifyContent: 'space-between' }}>
          <span>{t('pro.scansThisMonth')}</span>
          <span className="num">{isPro ? t('pro.unlimited') : t('pro.scansLeft', { count: left, limit: p.scansLimit })}</span>
        </div>
        {!isPro && (
          <div className="bar" aria-hidden>
            <span style={{ width: `${Math.min(100, (p.scansUsed / Math.max(1, p.scansLimit)) * 100)}%` }} />
          </div>
        )}
      </div>

      {isPro ? (
        <>
          <button className="btn btn--soft btn--block" onClick={() => setOpen(true)}>
            <Icon name="plus" size={18} />
            {t('pro.extend')}
          </button>
          <p className="muted small" style={{ margin: 0 }}>
            {t('pro.manage')}
          </p>
        </>
      ) : (
        <button className="btn btn--primary btn--block" onClick={() => setOpen(true)}>
          <Icon name="sparkles" size={18} />
          {t('pro.upgrade')}
        </button>
      )}
      <UpgradeSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
